import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { ReferenceValueSheetUpdateValidResponse } from 'src/app/cycle/models/flock-registration-service.model';
import { FlockRegistrationService } from 'src/app/cycle/services/flock-registration.service';

import * as refValSheetActions from './reference-value-sheet.actions';

import { getSelectedCountry } from './reference-value-sheet.selectors';
import * as settingActions from '../../settings/store/actions/setting.actions';
import { PoultryContextKeys } from '../../shared/constants';
import { SettingsServiceHelper } from '../../shared/services/settingsHelperService';
import {
  AnimalType,
  ColumnSettings,
  ReferenceValueSheet,
  RefValSheetModuleState,
  UpsertRefValSheetResponse,
} from '../models/reference-value-sheet.model';
import { ReferenceValueSheetService } from '../services/reference-value-sheet.service';

@Injectable()
export class ReferenceValueSheetEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<RefValSheetModuleState>>(Store);
  private router = inject(Router);
  private dialogService = inject(DialogService);
  private readonly locale = inject<LocaleService>(LocaleService);
  private refValSheetService = inject(ReferenceValueSheetService);
  private flockRegistrationService = inject(FlockRegistrationService);
  private settingsService = inject(SettingsServiceHelper);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  /* LOAD DATA EFFECTS */
  loadRefValSheetData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadRefValSheetData),
      concatMap(() =>
        of(
          refValSheetActions.clearRefValSheetState(),
          refValSheetActions.loadSelectedCountryRequest(),
          refValSheetActions.loadRefValSheetsRequest(),
          refValSheetActions.loadColumnSettings(),
          settingActions.loadRVSSettings(),
          settingActions.loadDayOffset(),
          settingActions.loadUnitOfMeasureSettings(),
        ),
      ),
    ),
  );
  loadStandardRefValSheetData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadStandardRefValSheetData),
      concatMap(() =>
        of(
          refValSheetActions.clearRefValSheetState(),
          refValSheetActions.loadSelectedCountryRequest(),
          refValSheetActions.loadStandardRefValSheetsRequest(),
          refValSheetActions.loadColumnSettings(),
          settingActions.loadRVSSettings(),
          settingActions.loadUnitOfMeasureSettings(),
        ),
      ),
    ),
  );

  loadSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadSelectedCountryRequest),
      exhaustMap((_) =>
        this.settingsService
          .getSingle(
            'BE',
            PoultryContextKeys.STANDARD_REFERENCE_VALUE_SHEETS_COUNTRY,
            SettingsStorage.Global,
            true,
          )
          .pipe(
            switchMap((settings) =>
              of(refValSheetActions.loadSelectedCountrySuccess({ id: settings })),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                refValSheetActions.loadRefValSheetsError({ error }),
                NotificationActions.errorSnackbar({ message: error.message }),
              ),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  setSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.setSelectedCountryRequest),
      exhaustMap((id) =>
        this.refValSheetService.getStandardReferenceValueSheets(id.id, AnimalType.BROILER).pipe(
          switchMap((refValSheets: ReferenceValueSheet[]) => {
            this.settingsService.setSettings(
              PoultryContextKeys.STANDARD_REFERENCE_VALUE_SHEETS_COUNTRY,
              id.id,
              SettingsStorage.Global,
              true,
            );

            return of(
              refValSheetActions.setSelectedCountrySuccess({
                id: id.id,
                refValSheets: refValSheets.filter((x) => x?.country === id.id),
              }),
            );
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              refValSheetActions.loadRefValSheetsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadRefValSheetsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadRefValSheetsRequest),
      mergeMap(() =>
        this.refValSheetService.getReferenceValueSheets().pipe(
          switchMap((refValSheets: ReferenceValueSheet[]) =>
            of(refValSheetActions.loadRefValSheetsSuccess({ refValSheets })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              refValSheetActions.loadRefValSheetsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadStandardRefValSheetsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadStandardRefValSheetsRequest),
      switchMap(() =>
        combineLatest([this.store.select(getSelectedCountry)]).pipe(
          skipWhile((selectedCountry) => selectedCountry[0] === undefined),
          take(1),
        ),
      ),
      mergeMap((country) =>
        this.refValSheetService
          .getStandardReferenceValueSheets(country.toString(), AnimalType.BROILER)
          .pipe(
            switchMap((refValSheets: ReferenceValueSheet[]) =>
              of(
                refValSheetActions.loadRefValSheetsSuccess({
                  refValSheets: refValSheets.filter((rvs) => rvs?.country === country.toString()),
                }),
              ),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                refValSheetActions.loadRefValSheetsError({ error }),
                NotificationActions.errorSnackbar({ message: error.message }),
              ),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  loadStandardRefValSheetsByCountryRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadStandardRefValSheetsByCountryRequest),
      switchMap(() =>
        combineLatest([this.store.select(UserSelectors.selectLocation)]).pipe(
          skipWhile((selectedLocation) => selectedLocation[0] === null),
          take(1),
        ),
      ),
      mergeMap((selectedLocation) =>
        this.refValSheetService
          .getStandardReferenceValueSheets(
            selectedLocation[0]?.country.toString(),
            AnimalType.BROILER,
          )
          .pipe(
            switchMap((refValSheets: ReferenceValueSheet[]) =>
              of(
                refValSheetActions.loadStandardRefValSheetsByCountrySuccess({
                  refValSheets: refValSheets.filter(
                    (rvs) => rvs?.country === selectedLocation[0]?.country.toString(),
                  ),
                }),
              ),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                refValSheetActions.loadStandardRefValSheetsByCountryFailed({ error }),
                NotificationActions.errorSnackbar({ message: error.message }),
              ),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  loadColumnSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.loadColumnSettings),
      switchMap((_) =>
        this.settingsService
          .getSingle(
            ColumnSettings.COLUMN_SETTINGS_ARRAY_DEFAULT,
            PoultryContextKeys.RVS_COLUMN_SETTINGS,
            SettingsStorage.Global,
            true,
          )
          .pipe(
            map((value: string[]) =>
              refValSheetActions.setColumnSettings({
                value,
                save: false,
              }),
            ),
            catchError(() => [
              CoreActions.fatalError({
                code: 500,
                message: this.locale.translate('general-settings-error'),
              }),
            ]),
          ),
      ),
    ),
  );

  setBaseRefValSheetRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.setBaseRefValSheetRequest),
      exhaustMap((baseRefValSheet) => {
        this.settingsService.setSettings(
          PoultryContextKeys.REFERENCE_VALUE_SHEET,
          baseRefValSheet.refValSheet,
          SettingsStorage.AppLocal,
        );
        return of(refValSheetActions.setBaseRefValSheetSuccess(baseRefValSheet));
      }),
    ),
  );

  /* NAVIGATION EFFECTS */
  createFromStandardRefValSheetNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.createFromStandardRefValSheetNavigation),
        tap((action) => {
          if (action.isStandard) {
            this.router.navigate(['standard-reference-value-sheet', 'create']);
          } else {
            this.router.navigate(['reference-value-sheet', 'create-from-standard']);
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  createRefValSheetNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.createRefValSheetNavigation),
        tap((action) => {
          refValSheetActions.setBaseRefValSheetRequest({ refValSheet: null });
          this.router.navigate([
            `${action.isStandard ? 'standard-' : ''}reference-value-sheet`,
            'create',
          ]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  editRefValSheetNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.editRefValSheetNavigation),
        tap((action) => {
          this.router.navigate([
            `${action.isStandard ? 'standard-' : ''}reference-value-sheet`,
            'edit',
            action.refValSheet.id,
          ]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  viewRefValSheetNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.viewRefValSheetNavigation),
        tap((action) => {
          this.router.navigate([
            `${action.isStandard ? 'standard-' : ''}reference-value-sheet`,
            'view',
            action.refValSheet.id,
          ]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  /* UPSERT EFFECTS */
  upsertRefValSheetRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.upsertRefValSheetRequest),
      exhaustMap(({ refValSheet, upsertType }) =>
        this.refValSheetService.upsertReferenceValueSheet(refValSheet).pipe(
          map((response: UpsertRefValSheetResponse) =>
            refValSheetActions.upsertRefValSheetSuccess({
              refValSheet: {
                ...refValSheet,
                id: response.id,
              },
              upsertType,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              refValSheetActions.upsertRefValSheetError({ error }),
              refValSheetActions.loadRefValSheetsRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
  upsertRefValSheetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.upsertRefValSheetSuccess),
        tap((action) => {
          this.router.navigate([
            `${
              action.refValSheet.isStandardReferenceValueSheet ? 'standard-' : ''
            }reference-value-sheet`,
          ]);
          if (action.upsertType === 'copyAsNew') {
            // reloading the page to make sure the current list of RefValSheets is shown.
            // (this was an issue on production)
            window.location.reload();
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  setColumnSettings$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refValSheetActions.setColumnSettings),
        tap((action) => {
          if (action.save === true) {
            this.settingsService.setSettings(
              PoultryContextKeys.RVS_COLUMN_SETTINGS,
              action.value,
              SettingsStorage.Global,
              true,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  /* DELETE EFFECTS */
  deleteRefValSheetConfirm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.deleteRefValSheetConfirm),
      exhaustMap(({ refValSheet }) =>
        combineLatest([
          this.flockRegistrationService.checkUpdateValidityReferenceValueSheet(refValSheet),
          of(refValSheet),
        ]).pipe(take(1)),
      ),
      switchMap(([response, refValSheet]) =>
        this.dialogService
          .confirmWarning({
            text: (response as ReferenceValueSheetUpdateValidResponse).isUsed
              ? this.locale.translate(`reference-value-sheet.dialog.deleteConfirmWhenInUse`)
              : this.locale.translate(`reference-value-sheet.dialog.deleteConfirm`),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  refValSheetActions.deleteRefValSheetRequest({
                    refValSheetId: refValSheet.id,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteRefValSheetRequest: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(refValSheetActions.deleteRefValSheetRequest),
      mergeMap((action) =>
        this.refValSheetService.deleteReferenceValueSheet(action.refValSheetId).pipe(
          map(() =>
            refValSheetActions.deleteRefValSheetSuccess({ refValSheetId: action.refValSheetId }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              refValSheetActions.deleteRefValSheetError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
